import { TimeReportSeries } from '../types/models/reports/time-report-series';
import { browserTimeZone, MAX_SERIES_CALENDAR_DAYS, minutesToIsoDuration } from './time-report-series.utils';

/** One day of a series, written as an independent regular report. */
export interface RemoteReportOccurrence {
  localDate: string;
  date: string;
  dateFrom?: string;
  dateTo?: string;
  duration: string;
}

export type SeriesOccurrenceErrorKind = 'invalidSeries' | 'dstLocalTime';

/** Raised before any write when a series cannot be expanded into unambiguous instants. */
export class SeriesOccurrenceError extends Error {
  public readonly kind: SeriesOccurrenceErrorKind;
  public readonly localDate?: string;
  public readonly localTime?: string;

  public constructor(kind: SeriesOccurrenceErrorKind, localDate?: string, localTime?: string) {
    super(kind === 'dstLocalTime' ? 'Ambiguous or missing local time' : 'Invalid series');
    this.name = 'SeriesOccurrenceError';
    this.kind = kind;
    this.localDate = localDate;
    this.localTime = localTime;
  }
}

const DAY = 86_400_000;

function calendarDate(value: string): number {
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    !Number.isFinite(timestamp) ||
    new Date(timestamp).toISOString().slice(0, 10) !== value
  ) {
    throw new SeriesOccurrenceError('invalidSeries');
  }
  return timestamp;
}

function clockMinutes(value: string): number {
  if (!/^([01]\d|2[0-3]):[0-5]\d:00$/.test(value)) {
    throw new SeriesOccurrenceError('invalidSeries');
  }
  return Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
}

function clockLabel(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * Resolves a wall-clock time on a calendar day in `zone` to a UTC instant.
 * Rejects times that do not exist (spring gap) or occur twice (autumn overlap).
 */
function instant(day: number, minutes: number, zone: string): string {
  const naive = day + minutes * 60_000;
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const wall = (timestamp: number): number => {
    const parts = Object.fromEntries(formatter.formatToParts(timestamp).map(part => [part.type, part.value]));
    return Date.UTC(
      +parts['year'],
      +parts['month'] - 1,
      +parts['day'],
      +parts['hour'],
      +parts['minute'],
      +parts['second']
    );
  };
  const offsets = new Set([-DAY, 0, DAY].map(delta => wall(naive + delta) - (naive + delta)));
  const matches = [...offsets].map(offset => naive - offset).filter(candidate => wall(candidate) === naive);
  if (matches.length !== 1) {
    throw new SeriesOccurrenceError('dstLocalTime', new Date(day).toISOString().slice(0, 10), clockLabel(minutes));
  }
  return new Date(matches[0]).toISOString();
}

/**
 * Expands a series into independent per-day writes, resolved in the browser's time zone by default.
 * Manual days are anchored at local noon; Range days carry the gross from/to instants.
 * Throws {@link SeriesOccurrenceError} before any write if the series is invalid or a time is DST-ambiguous.
 */
export function remoteReportOccurrences(
  series: TimeReportSeries,
  timeZone: string = browserTimeZone()
): RemoteReportOccurrence[] {
  const from = calendarDate(series.localDateFrom);
  const to = calendarDate(series.localDateTo);
  const count = (to - from) / DAY + 1;
  if (
    count < 1 ||
    count > MAX_SERIES_CALENDAR_DAYS ||
    !Number.isInteger(series.includedWeekdays) ||
    series.includedWeekdays < 1 ||
    series.includedWeekdays > 127
  ) {
    throw new SeriesOccurrenceError('invalidSeries');
  }

  const isRange = series.dailyStartTime !== undefined || series.dailyEndTime !== undefined;
  const start = isRange ? clockMinutes(series.dailyStartTime ?? '') : 0;
  const end = isRange ? clockMinutes(series.dailyEndTime ?? '') : 0;
  if (isRange && end <= start) {
    throw new SeriesOccurrenceError('invalidSeries');
  }

  const result: RemoteReportOccurrence[] = [];
  for (let day = from; day <= to; day += DAY) {
    const weekday = new Date(day).getUTCDay();
    if (!(series.includedWeekdays & (weekday === 0 ? 64 : 1 << (weekday - 1)))) {
      continue;
    }
    const dateFrom = isRange ? instant(day, start, timeZone) : undefined;
    const dateTo = isRange ? instant(day, end, timeZone) : undefined;
    const duration =
      dateFrom && dateTo
        ? minutesToIsoDuration((Date.parse(dateTo) - Date.parse(dateFrom)) / 60_000)
        : series.hoursPerDay;
    if (!duration) {
      throw new SeriesOccurrenceError('invalidSeries');
    }
    result.push({
      localDate: new Date(day).toISOString().slice(0, 10),
      date: instant(day, 12 * 60, timeZone),
      dateFrom,
      dateTo,
      duration
    });
  }

  if (!result.length) {
    throw new SeriesOccurrenceError('invalidSeries');
  }
  return result;
}
